import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";

/**
 * The page's one recurring object: a link as it reads in the dashboard —
 * slug → destination · status code · last edited. Every job section renders
 * the same row and adds exactly one control to it (a new destination, an
 * expiry pill, its matching order, campaign tags), so the page argues "same
 * link, more control" without a single animated device.
 *
 * Decorative by contract: callers wrap the sheet in aria-hidden and provide
 * an sr-only summary (honesty rules for mock UI).
 */
export default function LinkRow({
  slug,
  destination,
  code = 302,
  meta,
  extras,
  tone = "default",
  muted = false,
  highlightDestination = false,
  wrapDestination = false,
  highlightSlug = false,
  highlightCode = false,
}: {
  slug: string;
  destination: ReactNode;
  code?: number | string;
  /** Right-hand mono note, e.g. "edited just now". */
  meta?: ReactNode;
  /** Chips / pills rendered under the destination line. */
  extras?: ReactNode;
  tone?: "default" | "light";
  /** The superseded state — dimmed, struck through destination. */
  muted?: boolean;
  /** The changed field — brand-tinted so the eye lands on what moved. */
  highlightDestination?: boolean;
  /** Let a long destination wrap (e.g. appended query parameters) instead of truncating. */
  wrapDestination?: boolean;
  /** The renamed field — brand-tinted slug. */
  highlightSlug?: boolean;
  /** The changed status code — brand-tinted chip. */
  highlightCode?: boolean;
}) {
  const light = tone === "light";
  const ink = light ? "text-white" : "text-ink";
  const dim = light ? "text-white/45" : "text-dim";
  const sub = light ? "text-white/70" : "text-muted";
  const chip = highlightCode
    ? light
      ? "border-brand-500/50 bg-brand-500/15 text-brand-500"
      : "border-brand-600/40 bg-brand-600/[0.08] text-brand-600"
    : light
      ? "border-white/15 bg-white/[0.06] text-white/80"
      : "border-line-2 bg-surface-2 text-muted";

  return (
    <div
      className={`grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-2 px-[18px] py-[14px] ${
        muted ? "opacity-55" : ""
      }`}
    >
      <div className="min-w-0">
        <div className={`flex min-w-0 items-center gap-x-2.5 gap-y-1 font-jetbrains text-[13px] ${wrapDestination ? "flex-wrap" : "flex-nowrap"} ${ink}`}>
          <span className={`shrink-0 font-semibold ${highlightSlug ? (light ? "text-brand-500" : "text-brand-600") : ""}`}>{slug}</span>
          <ArrowRight size={13} aria-hidden className={`shrink-0 ${dim}`} />
          <span
            className={`min-w-0 ${wrapDestination ? "break-all" : "truncate"} ${
              muted ? `line-through decoration-1 ${sub}` : highlightDestination ? "text-brand-600" : sub
            } ${highlightDestination && light ? "text-brand-500" : ""}`}
          >
            {destination}
          </span>
        </div>
        {extras && <div className="mt-2 flex flex-wrap items-center gap-1.5">{extras}</div>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <span className={`rounded-md border px-[7px] py-[2px] font-jetbrains text-[11px] font-semibold ${chip}`}>
          {code}
        </span>
        {meta && <span className={`whitespace-nowrap font-jetbrains text-[11px] ${dim}`}>{meta}</span>}
      </div>
    </div>
  );
}

/** Small mono chip used by the row extras (order, expiry, campaign tags). */
export function RowChip({
  children,
  tone = "default",
  accent = false,
}: {
  children: ReactNode;
  tone?: "default" | "light";
  accent?: boolean;
}) {
  const cls = accent
    ? tone === "light"
      ? "border-brand-500/40 bg-brand-500/10 text-brand-500"
      : "border-brand-600/30 bg-brand-600/[0.07] text-brand-600"
    : tone === "light"
      ? "border-white/15 bg-white/[0.06] text-white/75"
      : "border-line-2 bg-surface text-muted";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-[2px] font-jetbrains text-[10.5px] ${cls}`}>
      {children}
    </span>
  );
}

/** The sheet the rows sit in — header bar + hairline-separated rows. */
export function LinkSheet({
  label,
  note,
  tone = "default",
  children,
  className = "",
  fill = false,
}: {
  label: ReactNode;
  note?: ReactNode;
  tone?: "default" | "light";
  children: ReactNode;
  className?: string;
  /** Stretch to the parent's height, rows stacked from the top (pair with `mt-auto` on a footer row). */
  fill?: boolean;
}) {
  const light = tone === "light";
  return (
    <div
      className={`overflow-hidden rounded-ref-lg border ${fill ? "flex h-full flex-col" : ""} ${
        light ? "border-white/12 bg-white/[0.04]" : "border-line bg-surface shadow-ref-lg"
      } ${className}`}
    >
      <div
        className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b px-[18px] py-[10px] font-jetbrains text-[10.5px] tracking-[0.12em] ${
          light ? "border-white/10 bg-white/[0.04] text-white/45" : "border-line bg-surface-2 text-dim"
        }`}
      >
        <span>{label}</span>
        {note && <span>{note}</span>}
      </div>
      <div className={`divide-y ${fill ? "flex flex-1 flex-col" : ""} ${light ? "divide-white/10" : "divide-line"}`}>{children}</div>
    </div>
  );
}
